// Pure 2D facial-geometry metric computations (pipeline Stage 2).
//
// Every function takes `pts` — an array of { x, y } points in a COMMON unit
// (pixels). The caller (index.analyzeGeometry) converts facePrep's
// output-fraction landmarks to pixels first, because x and y fractions are
// scaled by different frame dimensions (1080 vs 1440) and would otherwise be
// anisotropic. Outputs are dimensionless ratios / deviations / degrees, so the
// absolute pixel scale cancels.
import { LM, SYMMETRY_PAIRS } from './constants';

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

// Inter-pupillary distance — the scale-normalizer for symmetry.
function iod(pts) {
  const l = mid(pts[LM.L_EYE_OUTER], pts[LM.L_EYE_INNER]);
  const r = mid(pts[LM.R_EYE_INNER], pts[LM.R_EYE_OUTER]);
  return dist(l, r);
}

// Vertical face thirds (trichion→glabella→subnasale→menton). Returns the max
// absolute deviation of any third from the ideal 1/3 (0 = perfectly even).
export function thirdsDeviation(pts) {
  const yUpper = Math.abs(pts[LM.GLABELLA].y - pts[LM.TRICHION].y);
  const yMid = Math.abs(pts[LM.SUBNASALE].y - pts[LM.GLABELLA].y);
  const yLower = Math.abs(pts[LM.MENTON].y - pts[LM.SUBNASALE].y);
  const total = yUpper + yMid + yLower;
  if (total <= 0) return null;
  const ratios = [yUpper / total, yMid / total, yLower / total];
  return Math.max(...ratios.map((r) => Math.abs(r - 1 / 3)));
}

// Horizontal face fifths across left-edge, eyes, right-edge. Returns the max
// absolute deviation of any fifth from the ideal 1/5 (0 = perfectly even).
export function fifthsDeviation(pts) {
  // Order the six vertical dividers left→right by x to tolerate yaw labeling.
  const xs = [
    pts[LM.L_CHEEK].x,
    pts[LM.L_EYE_OUTER].x,
    pts[LM.L_EYE_INNER].x,
    pts[LM.R_EYE_INNER].x,
    pts[LM.R_EYE_OUTER].x,
    pts[LM.R_CHEEK].x,
  ].sort((a, b) => a - b);
  const total = xs[5] - xs[0];
  if (total <= 0) return null;
  let maxDev = 0;
  for (let i = 0; i < 5; i += 1) {
    const frac = (xs[i + 1] - xs[i]) / total;
    maxDev = Math.max(maxDev, Math.abs(frac - 1 / 5));
  }
  return maxDev;
}

// Canthal tilt in degrees, averaged across both eyes. Positive = outer canthus
// higher than inner (the desirable "upturned" direction). y grows downward.
export function canthalTiltDeg(pts) {
  const tilt = (inner, outer) => {
    const dy = pts[inner].y - pts[outer].y; // outer higher → positive
    const dx = Math.abs(pts[outer].x - pts[inner].x);
    if (dx <= 0) return null;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  };
  const l = tilt(LM.L_EYE_INNER, LM.L_EYE_OUTER);
  const r = tilt(LM.R_EYE_INNER, LM.R_EYE_OUTER);
  if (l == null || r == null) return null;
  return (l + r) / 2;
}

// Facial width-to-height ratio: bizygomatic width / (brow→upper-lip height).
export function fwhr(pts) {
  const width = dist(pts[LM.L_CHEEK], pts[LM.R_CHEEK]);
  const height = Math.abs(pts[LM.UPPER_LIP_TOP].y - pts[LM.GLABELLA].y);
  if (height <= 0) return null;
  return width / height;
}

// Eye separation ratio: inter-pupillary distance / bizygomatic width. Distinct
// from the fifths read (which measures the inner-canthal gap as a fifth).
export function esr(pts) {
  const width = dist(pts[LM.L_CHEEK], pts[LM.R_CHEEK]);
  if (width <= 0) return null;
  return iod(pts) / width;
}

// Lip balance: lower-lip height / upper-lip height (ideal ≈ 1.6).
export function lipRatio(pts) {
  const upper = Math.abs(pts[LM.UPPER_LIP_BOT].y - pts[LM.UPPER_LIP_TOP].y);
  const lower = Math.abs(pts[LM.LOWER_LIP_BOT].y - pts[LM.LOWER_LIP_TOP].y);
  if (upper <= 0) return null;
  return lower / upper;
}

// Bilateral symmetry deviation: for each mirror pair, compare each point's
// distance from the facial midline (x) and their vertical offset, normalized by
// IOD. Returns the mean per-pair deviation (0 = perfectly symmetric).
export function symmetryDeviation(pts) {
  const scale = iod(pts);
  if (scale <= 0) return null;
  // Midline x from stable central landmarks (nasion, menton, trichion).
  const midX =
    (pts[LM.NASION].x + pts[LM.MENTON].x + pts[LM.TRICHION].x) / 3;
  let sum = 0;
  SYMMETRY_PAIRS.forEach(([a, b]) => {
    const dxDev = Math.abs(Math.abs(pts[a].x - midX) - Math.abs(pts[b].x - midX));
    const dyDev = Math.abs(pts[a].y - pts[b].y);
    sum += (dxDev + dyDev) / scale;
  });
  return sum / SYMMETRY_PAIRS.length;
}

// Compute every raw metric value. Returns { key: value|null }.
export function computeMetrics(pts) {
  return {
    thirds: thirdsDeviation(pts),
    fifths: fifthsDeviation(pts),
    canthal_tilt: canthalTiltDeg(pts),
    fwhr: fwhr(pts),
    esr: esr(pts),
    symmetry: symmetryDeviation(pts),
    lip_ratio: lipRatio(pts),
  };
}
